<template>
  <default-layout>
    <main-container size="big" class="project-settings-layout">
      <!-- HEADER -->
      <div class="project-settings-layout__header">
        <sample-ribbon v-if="name && $permissions.isSampleInternalProjectID(id)" size="medium" />
        <i v-if="name" class="ddi ddi-project" />
        <h3 @click="goToProject()">{{ name }}</h3>
        <dd-button
          :color="ColorAssistant.ProjectColor"
          size="medium"
          @click="goToProject()"
        >
          <span>Go to Project</span>&nbsp;
          <i class="ddi ddi-arrow-right" />
        </dd-button>
      </div>

      <div class="project-settings-layout__body-wrapper">
        <!-- SIDEBAR -->
        <div class="project-settings-layout__sidebar">
          <dd-sidebar :elements="sidebarElements" title="Project Settings" />
        </div>

        <!-- MAIN -->
        <main class="project-settings-layout__main">
          <slot />
        </main>
      </div>
    </main-container>
  </default-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import * as routerNames from '@/services/router/router.names'
import MainContainer from '@/layouts/main-container.vue'
import DefaultLayout from '@/layouts/default-layout.vue'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { INavMenuGroup } from '@/types/data-objects/global'

/* API */

export default defineComponent({
  name: 'EntitySettingsLayout',

  components: {
    MainContainer,
    DefaultLayout,
  },

  props: {
    id: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      routerNames,
      ColorAssistant,
    }
  },

  computed: {
    sidebarElements(): INavMenuGroup[] {
      const items = [
        {
          label: 'Manage',
          options: [
            {
              value: routerNames.PROJECT_SETTINGS_ASSIGN_PEOPLE,
              label: 'Add/Remove Users',
              icon: 'people',
              isActive:
                this.$route.name === routerNames.PROJECT_SETTINGS_ASSIGN_PEOPLE,
            },
            ...(this.$permissions.isTieredPricingPlan
              ? [
                  {
                    value: routerNames.PROJECT_SETTINGS_ASSIGN_GUESTS,
                    label: 'Add/Remove Guests',
                    icon: 'people',
                    isActive:
                      this.$route.name ===
                      routerNames.PROJECT_SETTINGS_ASSIGN_GUESTS,
                  },
                ]
              : []),
            {
              value: routerNames.PROJECT_SETTINGS_CHECKLIST_TEMPLATES,
              label: 'Checklist Templates',
              icon: 'everything',
              isActive:
                this.$route.name ===
                routerNames.PROJECT_SETTINGS_CHECKLIST_TEMPLATES,
            },
            {
              value: routerNames.PROJECT_SETTINGS_CUSTOM_FIELDS,
              label: 'Custom Fields',
              icon: 'three-blocks',
              isActive:
                this.$route.name === routerNames.PROJECT_SETTINGS_CUSTOM_FIELDS,
            },
            {
              value: routerNames.PROJECT_SETTINGS_DESCRIPTION_TEMPLATES,
              label: 'Description Templates',
              icon: 'description',
              isActive:
                this.$route.name ===
                routerNames.PROJECT_SETTINGS_DESCRIPTION_TEMPLATES,
            },
            {
              value: routerNames.PROJECT_SETTINGS_EMAIL_SETTINGS,
              label: 'Email Settings',
              icon: 'incoming-email',
              isActive:
                this.$route.name ===
                routerNames.PROJECT_SETTINGS_EMAIL_SETTINGS,
            },
            {
              value: routerNames.PROJECT_SETTINGS_MANAGE_WORKFLOW,
              label: 'Manage Workflow',
              icon: 'workflows',
              isActive:
                this.$route.name ===
                routerNames.PROJECT_SETTINGS_MANAGE_WORKFLOW,
            },
            {
              value: routerNames.PROJECT_SETTINGS_WORKFLOW_AUTOMATIONS,
              label: 'Workflow Automations',
              icon: 'workflows',
              isActive:
                this.$route.name ===
                routerNames.PROJECT_SETTINGS_WORKFLOW_AUTOMATIONS,
            },
            {
              value: routerNames.PROJECT_SETTINGS_GENERAL_SETTINGS,
              label: 'General Settings',
              icon: 'cog',
              isActive:
                this.$route.name ===
                routerNames.PROJECT_SETTINGS_GENERAL_SETTINGS,
            },
            {
              value: routerNames.PROJECT_SETTINGS_INTEGRATIONS,
              label: 'Integrations',
              icon: 'integrations',
              isActive:
                this.$route.name === routerNames.PROJECT_SETTINGS_INTEGRATIONS,
            },
          ],
        },
      ]

      return items
    },
  },

  methods: {
    goToProject() {
      RouteAssistant.goToProjectLanding(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
        this.$permissions,
      )
    },
  },
})
</script>

<style lang="scss">
.project-settings-layout {
  margin-bottom: 40px;

  &__header {
    display: flex;
    align-items: center;
    width: 100%;
    padding-bottom: 12px;
    margin: 0 16px 30px 16px;
    border-bottom: $border--solid-light;

    @include media-breakpoint-down(sm) {
      margin: 0 0 20px 0;
      padding-left: 16px;
      padding-right: 16px;
    }

    .sample-ribbon {
      margin-top: -16px;
      margin-left: -34px;

      @include media-breakpoint-down(sm) {
        margin-left: 0;
      }
    }

    > .ddi {
      cursor: pointer;
      font-size: 18px;
      margin-right: 8px;
      color: var(--color-text-third);
    }

    h3 {
      cursor: pointer;
      margin-bottom: 0;
      @include single-line-ellipsis;
    }

    > :last-child {
      margin-left: auto;
    }
  }

  &__body-wrapper {
    display: flex;
    flex-direction: row;

    @include media-breakpoint-down(sm) {
      flex-direction: column;
    }
  }

  &__sidebar {
    flex: 0 0 224px;
    margin-right: 36px;

    @include media-breakpoint-down(sm) {
      flex: none;
      width: 100%;
      margin-right: 0;
      margin-bottom: 16px;
    }
  }

  &__main {
    flex-grow: 1;
    max-width: 750px;

    @include media-breakpoint-down(sm) {
      max-width: 100%;
    }

    h2 {
      margin: 0;
    }

    .el-radio {
      &__label {
        @include lettering--p;
      }

      .el-radio__input.is-checked + .el-radio__label {
        color: var(--color-text-secondary);
      }
    }

    .el-radio + .el-radio {
      margin-left: 0 !important;
    }

    .el-card {
      overflow: visible;
      .el-card__body {
        padding: 24px;

        .dd-divider {
          margin-top: 24px;
          margin-bottom: 24px;
        }
      }
    }
  }
}
</style>
